// ABOUTME: Layout helpers shared by the card lists and their loading skeletons.
// ABOUTME: Turns a percentage cardWidth into a width that accounts for the gap between the cards of a row.

import { CardViewConfig } from '../types';

const PERCENTAGE_WIDTH = /^(\d+(?:\.\d+)?)%$/;

// The minimum height of a highlight card without `cardHeight`, shared by the card and its skeleton.
export const HIGHLIGHT_CARD_MIN_HEIGHT = 216;

/**
 * The width a card of a row takes so that a percentage `cardWidth` reads as a share of the row:
 * with "25%" four cards and their three gaps fill the line exactly. Each card gives up
 * `columnGap * (1 - P/100)`, which sums to the gaps of any row of `100/P` cards. Widths that are
 * not a percentage of the row (px, rem, calc…) are returned unchanged.
 */
export function resolveRowCardWidth(
    cardWidth: string | undefined,
    columnGap: string,
): string | undefined {
    const match = cardWidth?.trim().match(PERCENTAGE_WIDTH);
    if (!cardWidth || !match) return cardWidth;
    const percentage = Number(match[1]);
    if (percentage <= 0 || percentage >= 100) return cardWidth;
    const gapShare = Number((1 - percentage / 100).toFixed(4));
    return `calc(${cardWidth.trim()} - ${columnGap} * ${gapShare})`;
}

interface RowCardLayoutInput {
    cardWidth?: string;
    columnGap: string;
    highlight?: boolean;
    // Cards grow into the space left on their line; false keeps every card at its cardWidth.
    stretch?: boolean;
}

/**
 * The flex box of a card in a row list. Stretching cards fill their line: each grows into the
 * space left, with `cardWidth` as the basis the wrapping decision is made on, and a highlight tile
 * ignores `cardWidth` to span the line it sits on. Without stretching every card, highlight
 * included, keeps its `cardWidth`, so a line of fewer cards leaves the rest of the row empty.
 */
export function rowCardLayout({
    cardWidth,
    columnGap,
    highlight = false,
    stretch = true,
}: RowCardLayoutInput): { flex: string; minWidth: 0 } {
    const width = resolveRowCardWidth(cardWidth, columnGap);
    if (!stretch) return { flex: `0 0 ${width ?? 'auto'}`, minWidth: 0 };
    return { flex: `1 1 ${highlight || !width ? '0' : width}`, minWidth: 0 };
}

// Cards on one line of a row list with a percentage width, used to size the loading placeholder.
function cardsPerRow(cardConfig: CardViewConfig): number | undefined {
    if (cardConfig.direction !== 'row') return undefined;
    const match = cardConfig.cardWidth?.trim().match(PERCENTAGE_WIDTH);
    const percentage = match ? Number(match[1]) : 0;
    return percentage > 0 && percentage <= 100 ? Math.floor(100 / percentage) : undefined;
}

// Default number of placeholder cards when the configuration does not fix how many there are.
const DEFAULT_SKELETON_CARD_COUNT = 5;
// A column of cards shows up to `maxItems` placeholders, capped so a large maxItems does not
// paint a screenful of them.
const MAX_COLUMN_SKELETON_CARDS = 10;

/**
 * The text lines of a standard card: a title, plus a subtitle when a second display content type
 * is shown on its own line or a type line is configured. Their height (20 + 22px) outgrows the
 * 40px leading avatar, so the line count sets the height of a card without `cardHeight`.
 */
function standardCardTextLines(cardConfig: CardViewConfig): number {
    const displayed = cardConfig.displayContentTypes ?? [];
    const subtitle =
        (displayed.length >= 2 && !cardConfig.mergeTitle) || !!cardConfig.typeContentType;
    return subtitle ? 2 : 1;
}

/**
 * Props for a `cards` WidgetSkeleton laid out like the loaded card list: same flow, width, height
 * and gaps. The count is unknown until the query lands, so a row fills one line and a column shows
 * `maxItems` placeholders, never more than `maxItems`. A standard card without `cardHeight` is
 * drawn with its padding, avatar and text lines, so it is exactly as tall as the loaded one; a
 * highlight card without `cardHeight` keeps the highlight minimum height.
 */
// The leading element of a standard card: the avatar of a favorite card or the image thumbnail,
// else the type icon.
const CARD_AVATAR_SIZE = 40;
export const CARD_ICON_SIZE = 24;

export function cardSkeletonProps(
    cardConfig: CardViewConfig,
    // A favorite card always leads with an avatar, image or not.
    { favorite = false }: { favorite?: boolean } = {},
) {
    const perRow = cardsPerRow(cardConfig);
    const count =
        cardConfig.direction === 'row'
            ? Math.min(cardConfig.maxItems ?? Infinity, perRow ?? DEFAULT_SKELETON_CARD_COUNT)
            : Math.min(
                  cardConfig.maxItems ?? DEFAULT_SKELETON_CARD_COUNT,
                  MAX_COLUMN_SKELETON_CARDS,
              );
    const highlight = cardConfig.cardVariant === 'highlight';
    return {
        variant: 'cards' as const,
        count,
        direction: cardConfig.direction,
        cardWidth: cardConfig.cardWidth,
        cardHeight:
            cardConfig.cardHeight ?? (highlight ? `${HIGHLIGHT_CARD_MIN_HEIGHT}px` : undefined),
        textLines:
            cardConfig.cardHeight || highlight ? undefined : standardCardTextLines(cardConfig),
        leadingSize:
            favorite || cardConfig.imageSources.length > 0 ? CARD_AVATAR_SIZE : CARD_ICON_SIZE,
        rowGap: cardConfig.rowGap,
        columnGap: cardConfig.columnGap,
        cardBgColor: cardConfig.cardBgColor,
        cardTextColor: cardConfig.cardTextColor,
    };
}
